import { Component, effect, EventEmitter, input, Input, OnDestroy, OnInit, Output, output, signal } from '@angular/core';
import { SabisChatbotPopupComponent } from '../sabis-chatbot-popup/sabis-chatbot-popup.component';
import { CommonModule } from '@angular/common';
import { AppNetworkService } from '../shared/network/app-network.service';
import { Subscription } from 'rxjs';
import { ChatbotService } from '../chatbot-core/chatbot.service';
import { DraggableDirective } from '../shared/directives/draggable.directive';
import { InputService } from '../sabis-chatbot/input/input.service';
import { ChatbotConfig, ChatbotPopupService } from '../../public-api';
import { ChatbotConfigService } from '../chatbot-core/chatbot-config/chatbot-config.service';


@Component({
  selector: 'sabis-chatbot-floating-button',
  imports: [SabisChatbotPopupComponent, CommonModule, DraggableDirective],
  templateUrl: './sabis-chatbot-floating-button.component.html',
  styleUrl: './sabis-chatbot-floating-button.component.scss'
})
export class SabisChatbotFloatingButtonComponent implements OnInit, OnDestroy {
  @Input() set config(value: ChatbotConfig) {
    this._chatbotConfigService.setConfig(value);
    this.chatbotConfig = value;
  }

  @Input('customCssClass') customCssClass?: string;
  @Input('floatingButtonBottom') floatingButtonBottom: number = 90;
  @Input('floatingButtonRight') floatingButtonRight: number = 20;
  @Input('isSessionIdVisible') isSessionIdVisible: boolean = false;
  @Output() contentClick = new EventEmitter<{ href: string; category: string | null }>();

  public chatbotConfig: ChatbotConfig = {
    userFirstName: 'Jad',
    equationPlayerUrl: '',
    isCloseButtonVisible: true,
    isMinimizeButtonVisible: true
  };


  public isChatbotVisible: boolean = false;
  public isChatbotMinimized: boolean = false;
  public isNetworkOnline: boolean = false;
  public isResponseReady: boolean = false;
  public bottom = 90;
  public right = 20;
  public userFirstName: string = '';

  // Bound callback for the draggable directive
  //Used to prevent the click event from firing after a drag
  public onClickBound = () => this.onClick();

  // Used to send a boolean output to the parent component in web or mobile 
  // This output is then used to fix the Book width in ebook web
  public isChatbotVisibleSignal = signal(false);
  public chatbotVisibilityChanged = output<boolean>();

  private _networkEventSub: Subscription = new Subscription();
  private _chatbotEventSub: Subscription = new Subscription();

  constructor(
    private _networkService: AppNetworkService,
    private _chatbotService: ChatbotService,
    private _chatbotConfigService: ChatbotConfigService,
    private _chatbotPopupService: ChatbotPopupService,
    private _inputService: InputService
  ) {
    effect(() => {
      this.chatbotVisibilityChanged.emit(this.isChatbotVisibleSignal());
    });
  }

  ngOnInit(): void {
    this._networkEventSub = this._networkService.online$.subscribe((status) => {
      this.isNetworkOnline = status;
    });

    this._chatbotEventSub = this._chatbotService.isResponseReady$.subscribe(
      (value) => {
        this.isResponseReady = value;
      }
    );

    this.bottom = this.floatingButtonBottom;
    this.right = this.floatingButtonRight;

    this.userFirstName = this._chatbotConfigService.getConfig()!.userFirstName;

    this._chatbotPopupService.registerPopupComponent(this);
  }

  ngAfterViewInit(): void {
    this.userFirstName = this._chatbotConfigService.getConfig()!.userFirstName;
  }

  public onClick(): void {
    if (this.isChatbotVisible) {
      // Currently visible, so close it
      this.isChatbotVisible = false;
      this.isChatbotMinimized = false;
      this.isChatbotVisibleSignal.set(false);
    } else {
      // Currently hidden (minimized or closed), so show it
      this.isChatbotVisible = true;
      this.isChatbotMinimized = false;
      this.isChatbotVisibleSignal.set(true);
    }
    this._chatbotService.setIsResponseReady(false);
  }

  public onCloseChatbotPopup(): void {
    this.isChatbotVisible = false;
    this.isChatbotMinimized = false;
    this.isChatbotVisibleSignal.set(false);
    this._chatbotService.setIsResponseReady(false);
    this._inputService.clearPendingAppends();
  }

  public onMinimizeChatbotPopup(): void {
    this.isChatbotVisible = false;
    this.isChatbotMinimized = true;
    this.isChatbotVisibleSignal.set(false);
    this._chatbotService.setIsResponseReady(false);
  }

  /**
   * Opens the chatbot popup and sets the provided message in the input field.
   * The message is truncated to the maximum allowed characters if it exceeds that limit, regardless
   * of whether the chatbot is visible or not.
   * If the chatbot is not visible, it is made visible and the response ready state is reset.
   * @param message - The message to be set in the chatbot input field.
   */
  public onOpenChatbotPopup(message: string): void {
    if (!this.isChatbotVisible) {
      this.isChatbotVisible = true;
      this.isChatbotMinimized = false;
      this.isChatbotVisibleSignal.set(true);
      this._chatbotService.setIsResponseReady(false);
    }
    this._inputService.queueAppendToInput(message);
  }

  public onContentClick(event: { href: string; category: string | null }): void {
    this.contentClick.emit(event);
  }

  ngOnDestroy(): void {
    this._networkEventSub.unsubscribe();
    this._chatbotEventSub.unsubscribe();
  }
}
